import { z } from 'zod'
import { assertAck, flatCountEnvelope } from './envelope'
import { ApiError, type ApiClient } from './client'


/**
 * Data connectors — the `/api/connector/*` family plus the DS collector reads
 * (legacy Admin › Connectors, saas-console-docs/dataflow/pages/admin-connectors.md).
 *
 * Every shape here was CAPTURED on 2026-09-23 (docs/inventory/connectors-capture.md),
 * not guessed. What the capture established:
 *
 * - Lists use the flat envelope: `data` IS the array, `count` the server total.
 * - `connector_streams` is always `[]` and `connector_cfgvalidationlastseen` always
 *   null on the list; `get_config` carries `validation_last_triggered` instead.
 * - `/api/health` answers for EVERY scope's collector; `eps`/`map` carry only the
 *   `connector_id` sent (`EPS_<TYPE>_<connector_id>`, re-captured 2026-09-24).
 * - `mgmt/ds/<comp>/connectors/<connector_name>/status` returns `[{group, name, status}]`
 *   with `group` = `connector_id` — the connector's live process status.
 * - `get_config` returns sensitive values as `""`; sending `""` back on edit keeps
 *   the stored credential (operator-confirmed).
 * - Manifests ship DUMMY `defaultValue`s on sensitive fields — see connector-form.ts.
 * - `configure` answers `data.connector_id` (captured 2026-09-25) — required, the
 *   connection test is keyed by it.
 * - The connection test (captured 2026-09-25): `validate_config` starts it and
 *   `mgmt/ds/<comp>/connectors/config_validation/<connector_id>/status` reports
 *   PENDING → SUCCESS | FAILED with a per-stage `validation_traceback`.
 * - `signal` (captured 2026-09-25) starts / stops a connector by NAME
 *   (`signal: 'START' | 'STOP'`); a stopped connector lists `alive_status: 'STOPPED'`.
 */

export const CONNECTOR_SYSTEM_CLASS = 'ds'

export type ConnectorConfig = Record<string, unknown>

export const configuredConnector = z
  .object({
    connector_id: z.string(),
    connector_name: z.string(),
    connector_type: z.string(),
    comp_id: z.string(),
    alive_status: z.string().default(''),
    connector_cfgvalidationstatus: z.string().default(''),
    connector_cfgvalidationmsg: z.string().nullable().default(null),
    connector_cfgvalidationlastseen: z.number().nullable().default(null),
    connector_streams: z.array(z.string()).default([]),
    logo_img: z.string().default(''),
    sub_type: z.string().nullable().default(null),
    validation_check: z.boolean().default(false),
  })
  .passthrough()
export type ConfiguredConnector = z.output<typeof configuredConnector>
export const configuredListResponse = flatCountEnvelope(configuredConnector)

export const catalogueEntry = z.object({ connector_type: z.string(), logo_img: z.string().default('') }).passthrough()
export type CatalogueEntry = z.output<typeof catalogueEntry>
export const catalogueResponse = flatCountEnvelope(catalogueEntry)

export const collector = z
  .object({
    comp_id: z.string(),
    systemName: z.string().default(''),
    hostName: z.string().default(''),
    localIPv4Address: z.string().default(''),
    status: z.string().default(''),
    scopeId: z.array(z.string()).default([]),
    systemClassType: z.string().default(''),
    createTS: z.number().nullable().default(null),
  })
  .passthrough()
export type Collector = z.output<typeof collector>
export const componentsResponse = flatCountEnvelope(collector)

export const healthBucket = z
  .object({
    comp_id: z.string(),
    scope_id: z.string().default(''),
    system_name: z.string().default(''),
    create_tstamp: z.number(),
    last_updated: z.number(),
    eps: z.record(z.unknown()).default({}),
    map: z.record(z.unknown()).default({}),
  })
  .passthrough()
export type HealthBucket = z.output<typeof healthBucket>
export const healthResponse = flatCountEnvelope(healthBucket)

export const connectorStatusEntry = z
  .object({ group: z.string().default(''), name: z.string().default(''), status: z.string().default('') })
  .passthrough()
export type ConnectorStatusEntry = z.output<typeof connectorStatusEntry>
export const connectorStatusResponse = flatCountEnvelope(connectorStatusEntry)

/* ------------------------------------------------------ connection test */

/** One traceback entry: `{<stage name>: <detail>, status: 'OK' | 'FAILED'}` (captured). */
const validationStageEntry = z.record(z.unknown())

export const connectorValidation = z
  .object({
    /** epoch SECONDS (float) on the wire */
    lastSeen: z.number().nullable().default(null).transform((s) => (s === null ? null : new Date(Math.round(s * 1000)))),
    message: z.string().default(''),
    name: z.string().default(''),
    status: z.string().default(''),
    validation_traceback: z.array(validationStageEntry).default([]),
  })
  .passthrough()
export type ConnectorValidation = z.output<typeof connectorValidation>
export const connectorValidationResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: connectorValidation,
})

export interface ValidationStage {
  stage: string
  detail: string
  ok: boolean
}

const text = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v))

/** Traceback entries as rows: the stage is the one key that is not `status`. */
export function validationStages(traceback: readonly Record<string, unknown>[]): ValidationStage[] {
  return traceback.map((e, i) => {
    const keys = Object.keys(e).filter((k) => k !== 'status')
    const [first, ...rest] = keys
    const detail = first === undefined ? '' : [text(e[first]), ...rest.map((k) => `${k}: ${text(e[k])}`)].join(' · ')
    return { stage: first ?? `Stage ${i + 1}`, detail, ok: String(e.status ?? '').toUpperCase() === 'OK' }
  })
}

/* ------------------------------------------------------------- manifest */

export type ConnectorTextType = 'text' | 'number' | 'boolean'

/**
 * One manifest field, NORMALIZED: `validation.textType` is lifted to `textType`,
 * absent optionals become explicit (`null`, `[]`, `{}`), so the form code never
 * branches on "is the key there".
 */
export interface ConnectorField {
  fieldName: string
  displayName: string
  /** input · number · select · long text · password · input-list-fields · select-conditional-fields · input-generated (others render as text). */
  input_type: string
  /** 'Basic' | 'Advanced'. */
  fieldType: string
  defaultValue: unknown
  defaultValueTemplate: string | null
  templateContext: string[]
  formFieldContext: string[]
  editable: boolean
  required: boolean
  sensitive: boolean
  helperText: string
  placeholder: string
  textType: ConnectorTextType
  input_options: string[]
  /** input-list-fields: the fields of one item. */
  subFields: ConnectorField[]
  itemLabelBySubField: string | null
  /** select-conditional-fields: option -> the fields that option reveals. */
  fields: Record<string, ConnectorField[]>
}

const toTextType = (v: unknown): ConnectorTextType => (v === 'number' || v === 'boolean' ? v : 'text')

// Only 10 of the 78 types were captured: cosmetic keys tolerate null (→ '') and
// scalar options coerce to strings, so one odd manifest cannot sink its whole form.
const nullableText = z.string().nullish().transform((v) => v ?? '')
const nullableOptions = z.array(z.union([z.string(), z.number(), z.boolean()]).transform(String)).nullish().transform((v) => v ?? [])

export const connectorField: z.ZodType<ConnectorField, z.ZodTypeDef, unknown> = z.lazy(() =>
  z
    .object({
      fieldName: z.string(),
      displayName: z.string().default(''),
      input_type: z.string().default('input'),
      fieldType: z.string().default('Basic'),
      defaultValue: z.unknown().optional(),
      defaultValueTemplate: z.string().nullish(),
      templateContext: z.array(z.string()).default([]),
      formFieldContext: z.array(z.string()).default([]),
      editable: z.boolean().default(true),
      required: z.boolean().default(false),
      sensitive: z.boolean().default(false),
      helperText: nullableText,
      placeholder: nullableText,
      validation: z.object({ textType: z.string().optional() }).passthrough().optional(),
      input_options: nullableOptions,
      subFields: z.array(connectorField).default([]),
      itemLabelBySubField: z.string().nullish(),
      fields: z.record(z.array(connectorField)).default({}),
    })
    .passthrough()
    .transform(
      (f): ConnectorField => ({
        fieldName: f.fieldName,
        displayName: f.displayName,
        input_type: f.input_type,
        fieldType: f.fieldType,
        defaultValue: f.defaultValue,
        defaultValueTemplate: f.defaultValueTemplate ?? null,
        templateContext: f.templateContext,
        formFieldContext: f.formFieldContext,
        editable: f.editable,
        required: f.required,
        sensitive: f.sensitive,
        helperText: f.helperText,
        placeholder: f.placeholder,
        textType: toTextType(f.validation?.textType),
        input_options: f.input_options,
        subFields: f.subFields,
        itemLabelBySubField: f.itemLabelBySubField ?? null,
        fields: f.fields,
      }),
    ),
)

export const connectorManifestData = z
  .object({
    config_format: z.array(connectorField).default([]),
    doc_markdown_text: z.string().default(''),
    mode: z.string().nullable().default(null),
    validation_check: z.boolean().default(false),
  })
  .passthrough()

export interface ConnectorManifest {
  fields: ConnectorField[]
  /** The setup guide (markdown); '' when the type ships none. */
  guide: string
  mode: string | null
  validationCheck: boolean
}

export function toConnectorManifest(d: z.output<typeof connectorManifestData>): ConnectorManifest {
  return { fields: d.config_format, guide: d.doc_markdown_text, mode: d.mode, validationCheck: d.validation_check }
}

const manifestResponse = z.object({ status: z.string().optional(), message: z.string().optional(), data: connectorManifestData })
const configResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.record(z.unknown()).default({}),
})
const writeResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    data: z.object({ connector_id: z.string().optional() }).passthrough().nullable().optional(),
  })
  .passthrough()

/* ------------------------------------------------------------------ api */

export type ConnectorSignal = 'START' | 'STOP'

export interface ConnectorScope {
  clusterId: string
  scopeId: string
}
export interface ConnectorRef extends ConnectorScope {
  compId: string
  connectorId: string
}

export interface ConnectorsApi {
  configured: (q: ConnectorScope & { pageNo?: number; pageSize?: number; search?: string }) => Promise<{ connectors: ConfiguredConnector[]; total: number }>
  catalogue: (q: { clusterId: string }) => Promise<CatalogueEntry[]>
  manifest: (q: { clusterId: string; connectorType: string }) => Promise<ConnectorManifest>
  getConfig: (q: ConnectorRef) => Promise<ConnectorConfig>
  configure: (q: ConnectorScope & { compId: string; config: ConnectorConfig }) => Promise<{ connectorId: string }>
  editConfig: (q: ConnectorRef & { config: ConnectorConfig }) => Promise<{ connectorId: string }>
  remove: (q: ConnectorRef) => Promise<void>
  components: (q: { clusterId: string }) => Promise<Collector[]>
  health: (q: { clusterId: string; compId: string; connectorId: string; from: Date; to: Date }) => Promise<HealthBucket[]>
  status: (q: { clusterId: string; compId: string; connectorName: string }) => Promise<ConnectorStatusEntry[]>
  /** Start / stop a connector's process — by connector NAME, as captured. */
  signal: (q: { clusterId: string; compId: string; connectorName: string; signal: ConnectorSignal }) => Promise<void>
  /** Starts the connection test again; the verdict comes from `validationStatus`. */
  validateConfig: (q: { clusterId: string; compId: string; connectorId: string }) => Promise<void>
  validationStatus: (q: { clusterId: string; compId: string; connectorId: string }) => Promise<ConnectorValidation>
}

/** The health endpoint takes second-precision UTC without a zone, as captured. */
const utcSeconds = (d: Date) => d.toISOString().slice(0, 19)

export function createConnectorsApi(client: ApiClient): ConnectorsApi {
  const post = <T>(clusterId: string, path: string, body: unknown, schema: z.ZodType<T, z.ZodTypeDef, unknown>) =>
    client.request('POST', `/${clusterId}/api/connector/${path}`, { body, schema })
  return {
    configured: async ({ clusterId, scopeId, pageNo = 1, pageSize = 20, search = '' }) => {
      const res = await post(clusterId, 'configured_list', {
        system_class_type: CONNECTOR_SYSTEM_CLASS, pageno: pageNo, pagesize: pageSize,
        scope_id: scopeId, customer_admin: true, search_query: search,
      }, configuredListResponse)
      return { connectors: res.data, total: res.count }
    },
    catalogue: async ({ clusterId }) => {
      const res = await post(clusterId, 'list', { page: 1, system_class_type: CONNECTOR_SYSTEM_CLASS, customer_admin: true }, catalogueResponse)
      return res.data
    },
    manifest: async ({ clusterId, connectorType }) => {
      const res = await post(clusterId, 'get_manifest', {
        connector_type: connectorType, system_class_type: CONNECTOR_SYSTEM_CLASS, customer_admin: true,
      }, manifestResponse)
      return toConnectorManifest(res.data)
    },
    getConfig: async ({ clusterId, scopeId, compId, connectorId }) => {
      const res = await post(clusterId, 'get_config', {
        system_class_type: CONNECTOR_SYSTEM_CLASS, comp_id: compId, connector_id: connectorId, scope_id: scopeId, customer_admin: true,
      }, configResponse)
      return res.data
    },
    configure: async ({ clusterId, scopeId, compId, config }) => {
      const res = await post(clusterId, 'configure', {
        scope_id: scopeId, system_class_type: CONNECTOR_SYSTEM_CLASS, comp_id: compId, config, customer_admin: true,
      }, writeResponse)
      assertAck(res, 'Adding the connector')
      const connectorId = res.data?.connector_id
      if (!connectorId) throw new ApiError(502, 'The connector was saved but the server returned no connector id.', res)
      return { connectorId }
    },
    editConfig: async ({ clusterId, scopeId, compId, connectorId, config }) => {
      const res = await post(clusterId, 'edit_config', {
        scope_id: scopeId, connector_id: connectorId, system_class_type: CONNECTOR_SYSTEM_CLASS, comp_id: compId, config, customer_admin: true,
      }, writeResponse)
      assertAck(res, 'Saving the connector')
      return { connectorId: res.data?.connector_id ?? connectorId }
    },
    remove: async ({ clusterId, scopeId, compId, connectorId }) => {
      const res = await post(clusterId, 'delete', {
        system_class_type: CONNECTOR_SYSTEM_CLASS, comp_id: compId, connector_id: connectorId, scope_id: scopeId, customer_admin: true,
      }, writeResponse)
      assertAck(res, 'Deleting the connector')
    },
    components: async ({ clusterId }) => {
      const res = await client.request('GET', `/${clusterId}/mgmt/components/list?customer_admin=true&sys_class_type=${CONNECTOR_SYSTEM_CLASS}`, {
        schema: componentsResponse,
      })
      return res.data
    },
    health: async ({ clusterId, compId, connectorId, from, to }) => {
      const res = await client.request('POST', `/${clusterId}/api/health`, {
        body: {
          from: utcSeconds(from), to: utcSeconds(to), component: 'DS', id: compId,
          type: 'process', subtype: 'listener', connector_id: connectorId, customer_admin: true,
        },
        schema: healthResponse,
      })
      return res.data
    },
    status: async ({ clusterId, compId, connectorName }) => {
      const res = await client.request(
        'GET',
        `/${clusterId}/mgmt/${CONNECTOR_SYSTEM_CLASS}/${compId}/connectors/${encodeURIComponent(connectorName)}/status?customer_admin=true`,
        { schema: connectorStatusResponse },
      )
      return res.data
    },
    signal: async ({ clusterId, compId, connectorName, signal }) => {
      const res = await post(clusterId, 'signal', {
        system_class_type: CONNECTOR_SYSTEM_CLASS, comp_id: compId, connector_name: connectorName, signal, customer_admin: true,
      }, writeResponse)
      assertAck(res, signal === 'STOP' ? 'Stopping the connector' : 'Starting the connector')
    },
    validateConfig: async ({ clusterId, compId, connectorId }) => {
      const res = await post(clusterId, 'validate_config', {
        system_class_type: CONNECTOR_SYSTEM_CLASS, comp_id: compId, connector_id: connectorId, customer_admin: true,
      }, writeResponse)
      assertAck(res, 'Starting the connection test')
    },
    validationStatus: async ({ clusterId, compId, connectorId }) => {
      const res = await client.request(
        'GET',
        `/${clusterId}/mgmt/${CONNECTOR_SYSTEM_CLASS}/${compId}/connectors/config_validation/${encodeURIComponent(connectorId)}/status?customer_admin=true`,
        { schema: connectorValidationResponse },
      )
      return res.data
    },
  }
}
